import { Component, ChangeDetectorRef } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import * as Rx from 'rxjs'
import { map, filter, expand } from 'rxjs/operators'
import {
  Live,
  LiveViewerCount2,
  LiveList,
  AdminViewLive,
  ArchiveUploadLive,
} from 'src/lib/entity/live'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import {
  FirestoreAccess,
  INFINITE_SCROLL_PAGE_SIZE,
} from 'src/lib/firestore-access'
import { environment } from 'src/env/environment'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'

export type Menu =
  | 'video_list'
  | 'watch_video'
  | 'creator_videos'
  | 'create_video'
  | 'edit_video'
  | 'edit_archive'

export interface WebSocketSubjectMap {
  liveId: string
  subject: WebSocketSubject<any>
  countPollingTimer: Rx.Subscription
}

export interface WebSocketViewerCountObservableMap {
  liveId: string
  viewerCount$: Rx.Observable<any>
}

@Component({
  selector: 'admin-live-index',
  templateUrl: './live-index.component.html',
  styleUrls: ['./live-index.component.scss'],
})
export class AdminLiveIndexComponent {
  // 無限スクロールの再試行が必要な要素数の上限
  private INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE = 10

  baseURL = environment.baseURL

  // アップロードライブ用
  isShowUploadedLives = false
  archiveUploadLiveList: ArchiveUploadLive[]
  infiniteScrollParentSelector = '.admin-archive-upload-live-list-scroll-wrap'
  infiniteScrollLiveParentSelector = '.admin-live-list-scroll-wrap'
  infiniteScrollCreatorSelector = '.admin-creators-video-scroll-wrap'
  allRead: Boolean
  // データ取得中かどうかのフラグ
  isLoadingWithUploadedLives: Boolean = false

  // 無限スクロールでデータを取りきったかどうかのフラグ
  private allLiveRead: Boolean = false
  private allCreatorLiveRead: Boolean = false
  // データ取得中かどうかのフラグ
  isLoading: Boolean = false
  // 無限スクロールの起点となるドキュメントID
  private lastDocumentId: string | null = null

  selectedMagazineId: number

  detailType: Menu = 'video_list'
  selected: Menu = 'video_list'
  selectedLive: AdminViewLive
  selectLiveId: string

  // ライブ一覧データ
  videoList$: Rx.Observable<AdminViewLive[] | 'NOT_FOUND'>
  videoList: AdminViewLive[]

  //クリエイター別ライブ一覧データ
  videoListCreator: AdminViewLive[] //クリエタライブデータ

  // websocket
  private wsSubjectList = new Array<WebSocketSubjectMap>()
  wsViewerCountObservableList = new Array<WebSocketViewerCountObservableMap>()
  private wsReqMessage: Object = { action: 'get_count' }
  private countPollingMillisWs: number = 1000 * 10 // 10秒

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private firestoreAccess: FirestoreAccess,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.route.queryParams
      .pipe(filter((params) => params.filter))
      .subscribe((params) => {
        this.isShowUploadedLives = params.filter == 'upload'
      })

    this.route.params.subscribe((params) => {
      this.selectedMagazineId = params['magazineId']
        ? parseInt(params['magazineId'], 10)
        : null
      if (this.selectedMagazineId) {
        this.selected = 'creator_videos'
        this.detailType = 'creator_videos'
      } else {
        this.selected = 'video_list'
        this.detailType = 'video_list'
      }
    })
  }

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngOnInit() {
    this.lastDocumentId = null
    this.isLoading = true
    this.isLoadingWithUploadedLives = true
    if (this.selected == 'video_list') {
      this.getVideoList(this.lastDocumentId)
      this.fetchArchiveUploadLives().subscribe(this.uploadedLiveFetchObserver)
    }
    if (this.selected == 'creator_videos') {
      this.getCreatorVideoList(this.lastDocumentId)
      this.selectedMagazineId
        ? this.fetchArchiveUploadLives(this.selectedMagazineId).subscribe(
            this.uploadedLiveFetchObserver
          )
        : this.fetchArchiveUploadLives().subscribe(
            this.uploadedLiveFetchObserver
          )
    }
  }

  onCheckboxChange(isUpload: boolean) {
    if (isUpload) {
      const path =
        this.selected === 'video_list'
          ? '/admin/lives'
          : '/admin/creators/' + this.selectedMagazineId + '/lives'
      this.router.navigate([path], { queryParams: { filter: 'upload' } })
    } else {
      this.router.navigate([], { relativeTo: this.route })
    }
  }

  onScroll() {
    if (this.allRead || this.isLoadingWithUploadedLives) return
    this.isLoadingWithUploadedLives = true
    this.fetchArchiveUploadLives(
      null,
      this.archiveUploadLiveList[this.archiveUploadLiveList.length - 1]
    ).subscribe(this.uploadedLiveFetchObserver)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
  }

  onScrollLiveList() {
    if (this.allLiveRead || this.isLoading) return
    this.isLoading = true
    this.getVideoList(this.lastDocumentId)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
  }

  onCreatorsVideoScroll() {
    if (this.allCreatorLiveRead || this.isLoading) return
    this.isLoading = true
    this.getCreatorVideoList(this.lastDocumentId)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
  }

  onCreatorUploadedLivesScroll() {
    if (
      this.allRead ||
      this.selectedMagazineId == null ||
      this.isLoadingWithUploadedLives
    )
      return
    this.isLoadingWithUploadedLives = true
    this.fetchArchiveUploadLives(
      this.selectedMagazineId,
      this.archiveUploadLiveList[this.archiveUploadLiveList.length - 1]
    ).subscribe(this.uploadedLiveFetchObserver)
    // 強制的に再描画させる
    this.changeDetectorRef.detectChanges()
  }

  private getVideoList(lastDocumentId: string): void {
    const transform = (live: LiveList) => {
      const data = {
        ...live,
        maxViewCount: 0,
      } as AdminViewLive

      if (live.roomId) {
        this.firestoreAccess.getRoom(live.roomId).subscribe((room) => {
          data.maxViewCount = room.max_view_count ?? 0
        })
      }

      return data
    }

    MagliveApiClient.getLiveHistories(lastDocumentId)
      .pipe(
        // 表示用の型に変更する
        map((response) => {
          if (response !== 'NOT_FOUND') {
            this.lastDocumentId = response.lastDocumentId
            return response.items.map((live) => transform(live))
          }
          return []
        }),
        // 結果が少ないと無限スクロールが動かないので、再帰呼び出しを結果を補充する
        expand((lives) => {
          if (
            lives.length > 0 &&
            lives.length < this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE &&
            this.videoList.length < this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE
          ) {
            return MagliveApiClient.getLiveHistories(
              lives[lives.length - 1].liveId
            ).pipe(
              map((response) => {
                console.log(`expand`)
                if (response !== 'NOT_FOUND') {
                  this.lastDocumentId = response.lastDocumentId
                  return response.items.map((live) => transform(live))
                }

                return []
              })
            )
          } else {
            return Rx.EMPTY
          }
        })
      )
      .subscribe((x) => {
        this.videoList$ = Rx.of(x)

        if (this.videoList == null) {
          // 初めて取得の場合
          this.videoList = x.map((live) => {
            if (live.status == 'ON_AIR') {
              this.connectWebsocket(live)
            }
            if (live.monitoringToken)
              live.monitoringURL = this.getMonitoringURL(
                live.liveId,
                live.monitoringToken
              )
            return live
          })
        } else {
          //スクロールで取得の場合
          const newList = x.map((live) => {
            if (live.status == 'ON_AIR') {
              this.connectWebsocket(live)
            }
            if (live.monitoringToken)
              live.monitoringURL = this.getMonitoringURL(
                live.liveId,
                live.monitoringToken
              )
            return live
          })
          this.videoList = this.videoList.concat(newList)
          this.allLiveRead = x.length == 0 ? true : false
        }
        this.isLoading = false
      })
  }

  private getMonitoringURL(liveId: string, monitoringToken: string): string {
    return (
      this.baseURL.magliveWeb + 'lives/' + liveId + '?token=' + monitoringToken
    )
  }

  private getCreatorVideoList(lastDocumentId: string): void {
    const transform = (live: LiveList) => {
      const data = {
        ...live,
        maxViewCount: 0,
      } as AdminViewLive

      if (live.roomId) {
        this.firestoreAccess.getRoom(live.roomId).subscribe((room) => {
          data.maxViewCount = room.max_view_count ?? 0
        })
      }

      return data
    }

    MagliveApiClient.getLivesByMagazineId(
      lastDocumentId,
      this.selectedMagazineId
    )
      .pipe(
        // 表示用の型に変更する
        map((response) => {
          if (response !== 'NOT_FOUND') {
            this.lastDocumentId = response.lastDocumentId
            return response.items.map((live) => transform(live))
          }
          return []
        }),
        // 結果が少ないと無限スクロールが動かないので、再帰呼び出しを結果を補充する
        expand((lives) => {
          if (
            lives.length > 0 &&
            lives.length < this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE &&
            this.videoListCreator.length <
              this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE
          ) {
            return MagliveApiClient.getLivesByMagazineId(
              lives[lives.length - 1].liveId,
              this.selectedMagazineId
            ).pipe(
              map((response) => {
                if (response !== 'NOT_FOUND') {
                  this.lastDocumentId = response.lastDocumentId
                  return response.items.map((live) => transform(live))
                }

                return []
              })
            )
          } else {
            return Rx.EMPTY
          }
        })
      )
      .subscribe((lives) => {
        if (this.videoListCreator == null) {
          //初めての取得場合
          this.videoListCreator = lives.map((live) => {
            if (live.status == 'ON_AIR') {
              this.connectWebsocket(live)
            }
            if (live.monitoringToken)
              live.monitoringURL =
                this.baseURL.magliveWeb +
                'lives/' +
                live.liveId +
                '?token=' +
                live.monitoringToken
            return live
          })
        } else {
          //スクロールで取得の場合
          const newList = lives.map((data) => {
            const live = data as AdminViewLive
            if (live.monitoringToken)
              live.monitoringURL =
                this.baseURL.magliveWeb +
                'lives/' +
                live.liveId +
                '?token=' +
                live.monitoringToken
            return live
          })
          this.videoListCreator = this.videoListCreator.concat(newList)
          this.allCreatorLiveRead = lives.length == 0
        }
        this.isLoading = false
      })
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private uploadedLiveFetchObserver = {
    next: (lives: ArchiveUploadLive[]) => {
      const last =
        this.archiveUploadLiveList != null
          ? this.archiveUploadLiveList[this.archiveUploadLiveList.length - 1]
          : null
      if (last && last.liveId != lives[lives.length - 1]?.liveId) {
        if (lives.length > 0) {
          this.archiveUploadLiveList = this.archiveUploadLiveList.concat(lives)
        } else {
          this.allRead = true
        }
      } else {
        this.archiveUploadLiveList = lives
        if (lives.length < INFINITE_SCROLL_PAGE_SIZE) {
          this.allRead = true
        } else {
          this.allRead = false
        }
      }
      this.isLoadingWithUploadedLives = false
      return
    },
  }

  private fetchArchiveUploadLives = (
    magazineId?: number,
    last?: ArchiveUploadLive
  ) => {
    const transform = (live: Live) => {
      const data = {
        liveId: live.id,
        roomId: live.roomId,
        title: live.title,
        magazineId: live.magazineId,
        magazineName: live.magazineName,
        status: live.status,
        archiveUploadStatus: live.archiveUploadStatus,
        createdAt: live.createdAt,
        convertHlsStatus: null,
      } as ArchiveUploadLive

      if (live.roomId) {
        this.firestoreAccess.getRoom(live.roomId).subscribe((room) => {
          data.convertHlsStatus = room.convert_hls_status
        })
      }

      return data
    }

    return this.firestoreAccess
      .getArchiveUploadedLives(magazineId, null, last)
      .pipe(
        map((lives) => {
          return lives.map((live) => transform(live))
        }),
        // 結果が少ないと無限スクロールが動かないので、再帰呼び出しを結果を補充する
        expand((lives) => {
          if (lives != null) {
            if (
              lives.length > 0 &&
              lives.length < this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE &&
              this.archiveUploadLiveList.length <
                this.INIT_INFINITE_SCROLL_VIEW_ITEM_SIZE
            ) {
              return this.firestoreAccess
                .getArchiveUploadedLives(
                  magazineId,
                  null,
                  lives[lives.length - 1]
                )
                .pipe(
                  map((lives) => {
                    return lives.map((live) => transform(live))
                  })
                )
            } else {
              return Rx.EMPTY
            }
          } else {
            return Rx.EMPTY
          }
        })
      )
  }

  getMonitoringUrl(liveId: string) {
    MagliveApiClient.getMonitoringUrl(liveId).subscribe((x) => {
      if (x !== 'NOT_AUTHORIZED') {
        let live = this.videoList.find(
          (video: AdminViewLive) => video.liveId === liveId
        )
        if (live !== undefined) {
          live.monitoringURL = x.monitoringURL
          this.videoList.splice(
            this.videoList.findIndex(
              (video: AdminViewLive) => video.liveId === liveId
            ),
            1,
            live
          )
        }
      }
    })
  }

  /**
   * 配信情報取得APIからwebsocketトークンを取得し、websocketへ接続する
   * @param live
   */
  private connectWebsocket(live: AdminViewLive): void {
    MagliveApiClient.getBroadcastByUserId(live.liveId, live.userId)
      .pipe(
        map((watchData) => {
          if (
            watchData !== 'NOT_FOUND' &&
            this.wsSubjectList.find((x) => x.liveId == live.liveId) ===
              undefined
          ) {
            const subject = this.getViewerCountFromWebsocket(
              live.liveId,
              live.roomId,
              live.userId,
              watchData.websocketToken
            )
            this.wsSubjectList.push({
              liveId: live.liveId,
              subject: subject,
              // 10秒ごとにリクエストを送る (視聴者数を更新する)
              countPollingTimer: Rx.interval(
                this.countPollingMillisWs
              ).subscribe(() => {
                subject.next(this.wsReqMessage)
              }),
            })
          }
        })
      )
      .subscribe()
  }

  /**
   * websocket接続で視聴者数を取得する
   * @param roomId
   * @param userId
   * @param token
   * @returns
   */
  private getViewerCountFromWebsocket(
    liveId: string,
    roomId: string,
    userId: number | undefined,
    token: string
  ): WebSocketSubject<any> {
    if (userId === undefined) {
      userId = 0
    }
    const wsEndPoint = `${environment.baseURL.mag2WSDomain}?room_id=${roomId};user_id=${userId};token=${token}`
    const wsSubject = webSocket(wsEndPoint)
    console.log('[Debug:websocket] connected')

    wsSubject.next(this.wsReqMessage)

    wsSubject.subscribe(
      (x) => {
        if (typeof x === 'object') {
          const targetLives =
            this.selected == 'video_list'
              ? this.videoList
              : this.videoListCreator
          const index = targetLives.findIndex((item) => item.liveId == liveId)
          if (index !== -1) {
            const live = targetLives[index]
            live.currentPlaybackCount = (x as LiveViewerCount2).view_count
            targetLives[index] = live
          }
          console.log('[Debug:websocket] view count updated')
        }
      },
      (error) => console.error(error),
      () => console.log('[Debug:websocket] close')
    )
    return wsSubject
  }

  /**
   * websocketの接続を切る
   */
  private closeWebsocket(): void {
    if (this.wsSubjectList.length > 0) {
      this.wsSubjectList.forEach((item) => {
        item.subject.unsubscribe()
        item.countPollingTimer.unsubscribe()
      })
      this.wsSubjectList = []
    }
  }

  ngOnDestroy(): void {
    this.closeWebsocket()
  }
}
